import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ChatHistoryStorage, StoredChatMessage } from '@models/chat/chat-history.model';
import { CHAT_HISTORY_STORAGE_PREFIX } from '@constants/chat-history.constants';

@Injectable({ providedIn: 'root' })
export class ChatHistoryService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private buildKey(userId: string, roleId: string, projectId: number, epicCode?: string): string {
    const base = `${CHAT_HISTORY_STORAGE_PREFIX}_${userId}_${roleId}_${projectId}`;
    return epicCode ? `${base}_${epicCode}` : base;
  }

  // O próprio acesso a sessionStorage/localStorage pode lançar (modo privado, cota, política).
  private resolveStorage(storage: ChatHistoryStorage): Storage {
    return storage === 'session' ? sessionStorage : localStorage;
  }

  load(
    userId: string,
    roleId: string,
    projectId: number,
    epicCode?: string,
    storage: ChatHistoryStorage = 'local'
  ): StoredChatMessage[] {
    if (!this.isBrowser) return [];
    const key = this.buildKey(userId, roleId, projectId, epicCode);
    try {
      const raw = this.resolveStorage(storage).getItem(key);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as StoredChatMessage[]) : [];
    } catch {
      // Storage indisponível ou JSON inválido — o chat abre vazio, sem erro.
      return [];
    }
  }

  save(
    userId: string,
    roleId: string,
    projectId: number,
    messages: StoredChatMessage[],
    maxMessages: number,
    epicCode?: string,
    storage: ChatHistoryStorage = 'local'
  ): void {
    if (!this.isBrowser) return;
    const key = this.buildKey(userId, roleId, projectId, epicCode);
    const windowed =
      messages.length > maxMessages ? messages.slice(messages.length - maxMessages) : messages;
    try {
      this.resolveStorage(storage).setItem(key, JSON.stringify(windowed));
    } catch {
      // Storage indisponível — histórico não persiste, mas não quebra o chat.
    }
  }

  /** Sessão do agente (thread_id) mantida na aba; forceNew inicia uma conversa nova. */
  getOrCreateSessionId(key: string, forceNew = false): string {
    if (!this.isBrowser) return crypto.randomUUID();
    try {
      const stored = forceNew ? null : sessionStorage.getItem(key);
      if (stored) return stored;
      const created = crypto.randomUUID();
      sessionStorage.setItem(key, created);
      return created;
    } catch {
      // sessionStorage indisponível — sessão vale só enquanto a tela estiver aberta.
      return crypto.randomUUID();
    }
  }

  /** Remove da aba o histórico e as sessões do agente — o próximo usuário não herda o contexto. */
  clearSessionData(): void {
    if (!this.isBrowser) return;
    try {
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith(CHAT_HISTORY_STORAGE_PREFIX))
        .forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // sessionStorage indisponível — não há o que limpar.
    }
  }
}
